import { PublicClientApplication, InteractionRequiredAuthError, EventType } from '@azure/msal-browser';
import { msalConfig, b2cPolicies, loginRequest } from './authConfig';

// Create the MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promises
// Important for B2C to handle redirect properly after authentication
msalInstance.initialize().then(() => {
  // Account selection logic is app dependent. Adjust as needed for your app
  msalInstance.handleRedirectPromise()
    .then(response => {
      if (response) {
        console.log('Redirect response handled successfully', response);
      }
    })
    .catch(error => {
      console.error('Error handling redirect:', error);
    });
});

// Register event callbacks for auth events
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    console.log('Login success');
    
    // Check if event is caused by redirect
    if (event.payload.account) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  }
  
  // Handle B2C password reset flow
  if (event.eventType === EventType.LOGIN_FAILURE) {
    if (event.error && event.error.errorMessage.indexOf('AADB2C90118') > -1) {
      // Password reset error code from B2C
      try {
        msalInstance.loginRedirect({
          authority: b2cPolicies.authorities.resetPassword.authority,
          scopes: loginRequest.scopes
        });
      } catch (err) {
        console.error("Error handling password reset: ", err);
      }
    }
  }
});

// Helper function to get active account
export const getActiveAccount = () => {
  let activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
    // If there's no active account but there are accounts signed in
    // set the first one as active
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    activeAccount = msalInstance.getActiveAccount();
  }
  return activeAccount;
};

// Function to initiate login
export const signIn = async (method = 'popup') => {
  if (method === 'redirect') {
    return msalInstance.loginRedirect(loginRequest);
  } else {
    try {
      return await msalInstance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  }
};

// Function to initiate logout
export const signOut = async (method = 'popup') => {
  const account = getActiveAccount();
  const logoutRequest = {
    account,
    postLogoutRedirectUri: window.location.origin,
  };
  
  if (method === 'redirect') {
    return msalInstance.logoutRedirect(logoutRequest);
  } else {
    try {
      return await msalInstance.logoutPopup(logoutRequest);
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  }
};

// Function to get ID token
export const getTokenSilently = async (request) => {
  console.log('getTokenSilently called with B2C request');
  
  const account = getActiveAccount();
  if (!account) {
    console.error('No active account found. User needs to sign in first.');
    throw new Error('No active account! Sign in required.');
  }
  
  console.log('Active account found:', account.username || account.name || 'Unknown user');
  
  try {
    // Get the ID token instead of access token
    const response = await msalInstance.acquireTokenSilent({
      scopes: request.scopes,
      account
    });
    
    // Use the ID token
    const idToken = response.idToken;
    console.log('ID token acquired successfully (silent)');
    
    return idToken;
  } catch (error) {
    console.warn('Error during silent ID token acquisition:', error);
    
    // If silent acquisition fails, try interactive
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: request.scopes,
          account
        });
        
        const idToken = response.idToken;
        console.log('ID token acquired successfully (popup)');
        
        return idToken;
      } catch (interactiveError) {
        console.error('Error during interactive ID token acquisition:', interactiveError);
        throw interactiveError;
      }
    } else {
      console.error('Error during ID token acquisition:', error);
      throw error;
    }
  }
};
